import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, XCircle, Brain, Cog, Building2, Lock, Infinity as InfinityIcon, Cpu,
  Zap, Eye, BarChart3, Settings2, DollarSign, ShieldCheck, FileCheck,
} from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import AmbientParticles from "@/components/site/AmbientParticles";
import { WHY_ESTHERA_IMAGES, SOLUTIONS_IMAGES } from "@/lib/site";

const TRADITIONAL = [
  "Siloed data across disconnected systems",
  "Manual processes and repetitive tasks",
  "Reactive reporting with limited visibility",
  "One-size-fits-all. Not real estate specific",
  "Slow to adapt. Hard to scale",
  "High total cost of ownership",
];

const ESTHERA = [
  "Unified data on one intelligent platform",
  "AI-powered automation across workflows",
  "Real-time intelligence and full visibility",
  "Built exclusively for real estate",
  "Designed for scale. Built for the future",
  "Lower cost. Higher performance. More value",
];

const ADVANTAGE = [
  { icon: Brain,        title: "Unified Intelligence", desc: "One source of truth. AI unifies all data to create real-time clarity." },
  { icon: Cog,          title: "Automation First",     desc: "Intelligent automation eliminates manual work and accelerates results." },
  { icon: Building2,    title: "Built For Real Estate", desc: "Purpose-built for real estate workflows and industry complexity." },
  { icon: Lock,         title: "Enterprise Security",  desc: "Bank-grade security, role-based access, and full compliance." },
  { icon: InfinityIcon, title: "Infinite Scalability", desc: "Scale across teams, regions, and portfolios without limitations." },
  { icon: Cpu,          title: "Explainable AI",       desc: "Transparent AI models with human oversight at every step." },
];

const LEADERS = [
  { icon: Zap,         label: "Faster Response Times",   desc: "Engage leads instantly across every channel." },
  { icon: Eye,         label: "Greater Data Visibility", desc: "Complete visibility across your entire portfolio." },
  { icon: BarChart3,   label: "Higher Conversion Rates", desc: "Turn more leads into closed deals." },
  { icon: Settings2,   label: "Less Manual Work",        desc: "Automate repetitive tasks and save time." },
  { icon: DollarSign,  label: "Lower Operating Costs",   desc: "Reduce tools, licenses, and manual overhead." },
  { icon: ShieldCheck, label: "Stronger Governance",     desc: "Maintain control with security and compliance." },
  { icon: FileCheck,   label: "Verified Intelligence",   desc: "Every recommendation traces back to your data." },
];

const OUTCOMES = [
  { slug: "brokerages",  title: "Brokerages",  img: SOLUTIONS_IMAGES.cardBrokerages, desc: "Streamline lead management, improve agent productivity, and close more deals—faster.", bullets: ["Higher agent productivity","Better lead conversion","Stronger client relationships"] },
  { slug: "developers",  title: "Developers",  img: SOLUTIONS_IMAGES.cardDevelopers, desc: "Unify project data, control costs, and accelerate project delivery with confidence.",        bullets: ["On-time project delivery","Budget accuracy","Improved stakeholder visibility"] },
  { slug: "enterprises", title: "Enterprises", img: SOLUTIONS_IMAGES.cardEnterprises, desc: "Manage complex portfolios, mitigate risk, and drive smarter decisions at scale.",            bullets: ["Portfolio optimization","Risk reduction","Stronger financial performance"] },
];

export default function WhyEstheraPage() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => setScrollY(window.scrollY)); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);
  const heroParallax = `translate3d(0, ${-scrollY * 0.14}px, 0)`;
  const ctaParallax  = `translate3d(0, ${ scrollY * 0.05}px, 0)`;

  return (
    <div className="min-h-screen bg-obsidian text-platinum relative overflow-x-hidden" data-testid="why-esthera-page">
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden><AmbientParticles count={34} tone="mixed" density={0.55} /></div>
      <Navbar />

      {/* HERO */}
      <section className="relative pt-[120px] lg:pt-[150px] pb-16 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none bg-grid opacity-[0.06]" aria-hidden />
        <div className="absolute -top-32 right-0 w-[820px] h-[820px] pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(96,165,250,0.20), transparent)" }} aria-hidden />
        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_1.3fr] gap-8 lg:gap-12 items-center min-h-[560px] lg:min-h-[660px]">
            <div className="fade-up relative z-10">
              <div className="eyebrow-positioning">WHY ESTHERA</div>
              <h1 className="font-display mt-5 text-platinum text-[44px] sm:text-[56px] lg:text-[64px] xl:text-[68px] leading-[1.04] tracking-[-0.022em]">
                Built Different.<br />
                <span className="text-sapphire-glow" style={{ textShadow: "0 0 36px rgba(96,165,250,0.40)" }}>Built For The Future.</span>
              </h1>
              <p className="mt-6 text-[16px] sm:text-[17px] text-platinum/72 leading-[1.7] max-w-[520px]">
                Why leading real estate organizations choose Esthera RealityOS over fragmented software stacks.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <a href="#advantage" data-testid="why-explore-advantage"
                  className="btn-primary rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2">
                  Explore The Advantage <ArrowRight className="w-4 h-4" />
                </a>
                <button onClick={() => navigate("/book-demo")} data-testid="why-book-demo"
                  className="btn-ghost rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2">
                  Book a Demo <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="relative lg:-mr-10 xl:-mr-20" data-testid="why-hero-visual"
              style={{ transform: heroParallax, willChange: "transform" }}>
              <div className="absolute inset-0 pointer-events-none"
                style={{ background: "radial-gradient(60% 55% at 55% 50%, rgba(37,99,235,0.32), transparent 72%)", filter: "blur(48px)" }} aria-hidden />
              <div className="relative">
                <img src={WHY_ESTHERA_IMAGES.hero} alt="Connected real estate intelligence — sapphire skyline with linked icons across the network"
                  className="block w-full h-[520px] lg:h-[680px] object-cover object-center"
                  style={{
                    WebkitMaskImage: "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.6) 80%, transparent 100%)",
                    maskImage: "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.6) 80%, transparent 100%)",
                  }} draggable="false" />
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.75) 0%, rgba(10,15,26,0) 18%, rgba(10,15,26,0) 70%, rgba(10,15,26,0.98) 100%)" }} aria-hidden />
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "linear-gradient(90deg, rgba(10,15,26,1) 0%, rgba(10,15,26,0.82) 14%, rgba(10,15,26,0.18) 32%, rgba(10,15,26,0) 50%, rgba(10,15,26,0) 78%, rgba(10,15,26,0.55) 100%)" }} aria-hidden />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRADITIONAL vs ESTHERA */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <h2 className="font-display text-center text-platinum text-[28px] sm:text-[34px] lg:text-[40px] leading-tight tracking-[-0.018em] fade-up">
            Traditional Software <span className="text-platinum/60">vs.</span> <span className="text-sapphire-glow" style={{ textShadow: "0 0 30px rgba(96,165,250,0.45)" }}>Esthera RealityOS</span>
          </h2>
          <div className="relative mt-10 rounded-2xl border border-white/[0.07] p-6 sm:p-10 lg:p-12 overflow-hidden"
            style={{ background: "linear-gradient(180deg, rgba(13,18,32,0.6) 0%, rgba(10,15,26,0.92) 100%)" }}>
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/40 to-transparent" />
            <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-8 lg:gap-10 items-center">
              {/* Traditional */}
              <div className="fade-up">
                <div className="text-[10.5px] tracking-[0.34em] uppercase font-medium text-red-400/85 mb-4">TRADITIONAL SOFTWARE</div>
                <ul className="space-y-3.5 mb-6">
                  {TRADITIONAL.map((t) => (
                    <li key={t} className="flex items-start gap-3 text-[13.5px] text-platinum/75 leading-[1.55]">
                      <XCircle className="w-4 h-4 mt-[3px] text-red-400/85 shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <div className="relative aspect-[4/3]">
                  <div className="absolute inset-[-6%] pointer-events-none"
                    style={{ background: "radial-gradient(closest-side, rgba(248,113,113,0.16), transparent 70%)", filter: "blur(30px)" }} aria-hidden />
                  <img src={WHY_ESTHERA_IMAGES.chaos} alt="Disconnected silos with red error indicators"
                    className="relative w-full h-full object-contain"
                    style={{
                      filter: "brightness(0.92) contrast(1.18) saturate(1.05)",
                      WebkitMaskImage: "radial-gradient(95% 95% at 50% 50%, #000 60%, rgba(0,0,0,0.5) 82%, transparent 100%)",
                      maskImage: "radial-gradient(95% 95% at 50% 50%, #000 60%, rgba(0,0,0,0.5) 82%, transparent 100%)",
                    }} draggable="false" />
                </div>
              </div>

              {/* VS divider */}
              <div className="hidden lg:flex flex-col items-center justify-center fade-up" style={{ animationDelay: "120ms" }}>
                <div className="w-14 h-14 rounded-full inline-flex items-center justify-center font-display text-platinum text-[14px] tracking-[0.18em]"
                  style={{ background: "radial-gradient(circle at 30% 30%, rgba(96,165,250,0.12), #0E1422 80%)", border: "1px solid rgba(96,165,250,0.45)" }}>
                  VS
                </div>
              </div>

              {/* Esthera */}
              <div className="fade-up" style={{ animationDelay: "180ms" }}>
                <div className="text-[10.5px] tracking-[0.34em] uppercase font-medium text-sapphire-glow mb-4"
                  style={{ textShadow: "0 0 12px rgba(96,165,250,0.55)" }}>ESTHERA REALITYOS</div>
                <ul className="space-y-3.5 mb-6">
                  {ESTHERA.map((t) => (
                    <li key={t} className="flex items-start gap-3 text-[13.5px] text-platinum/80 leading-[1.55]">
                      <CheckCircle2 className="w-4 h-4 mt-[3px] text-sapphire-glow shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <div className="relative aspect-[4/3]">
                  <div className="absolute inset-[-6%] pointer-events-none"
                    style={{ background: "radial-gradient(closest-side, rgba(37,99,235,0.40), transparent 72%)", filter: "blur(36px)" }} aria-hidden />
                  <img src={WHY_ESTHERA_IMAGES.unified} alt="Unified intelligence — crystal-cored city with sapphire workflow nodes"
                    className="relative w-full h-full object-contain breathe-glow"
                    style={{
                      mixBlendMode: "screen",
                      filter: "brightness(0.92) contrast(2.30) saturate(1.20)",
                      WebkitMaskImage: "radial-gradient(95% 95% at 50% 50%, #000 60%, rgba(0,0,0,0.5) 82%, transparent 100%)",
                      maskImage: "radial-gradient(95% 95% at 50% 50%, #000 60%, rgba(0,0,0,0.5) 82%, transparent 100%)",
                    }} draggable="false" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ADVANTAGE + LEADERS — split layout */}
      <section className="relative pb-16 lg:pb-20" id="advantage">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-5 lg:gap-6">
            {/* The Esthera Advantage */}
            <div className="relative rounded-2xl border border-white/[0.07] p-6 lg:p-8 fade-up overflow-hidden"
              style={{ background: "linear-gradient(180deg, rgba(13,18,32,0.55) 0%, rgba(10,15,26,0.92) 100%)" }}>
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/40 to-transparent" />
              <h3 className="font-display text-platinum text-[22px] sm:text-[26px] lg:text-[28px] leading-tight tracking-[-0.018em]">The Esthera Advantage</h3>
              <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-7">
                {ADVANTAGE.map((a, i) => {
                  const Icon = a.icon;
                  return (
                    <div key={a.title} className="text-center fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                      <div className="w-11 h-11 rounded-full mx-auto inline-flex items-center justify-center mb-3"
                        style={{
                          background: "radial-gradient(circle at 30% 30%, rgba(96,165,250,0.12), #0E1422 75%)",
                          border: "1px solid rgba(96,165,250,0.55)",
                          boxShadow: "0 0 16px -3px rgba(96,165,250,0.40)",
                        }}>
                        <Icon className="w-4.5 h-4.5 text-sapphire-glow" style={{ width: "18px", height: "18px" }} />
                      </div>
                      <div className="font-display text-platinum text-[13.5px] leading-[1.22] tracking-tight">{a.title}</div>
                      <p className="mt-1.5 text-[11.5px] text-platinum/60 leading-[1.55] px-1">{a.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Why Leaders Choose Esthera */}
            <div className="relative rounded-2xl border border-white/[0.07] p-6 lg:p-8 fade-up overflow-hidden"
              style={{ background: "linear-gradient(180deg, rgba(13,18,32,0.55) 0%, rgba(10,15,26,0.92) 100%)", animationDelay: "100ms" }}>
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/40 to-transparent" />
              <h3 className="font-display text-platinum text-[22px] sm:text-[26px] lg:text-[28px] leading-tight tracking-[-0.018em]">Why Leaders Choose Esthera</h3>
              <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-8">
                {LEADERS.map((l, i) => {
                  const Icon = l.icon;
                  const isLastOrphan = i === LEADERS.length - 1 && LEADERS.length % 3 === 1;
                  return (
                    <div
                      key={l.label}
                      className={`text-center fade-up${isLastOrphan ? " col-span-2 sm:col-span-1 sm:col-start-2" : ""}`}
                      style={{ animationDelay: `${i * 60}ms` }}
                    >
                      <div className="w-11 h-11 rounded-full mx-auto mb-3 inline-flex items-center justify-center"
                        style={{
                          background: "radial-gradient(circle at 30% 30%, rgba(96,165,250,0.16), #0E1422 75%)",
                          border: "1px solid rgba(96,165,250,0.55)",
                          boxShadow: "0 0 14px -3px rgba(96,165,250,0.45)",
                        }}>
                        <Icon className="w-[18px] h-[18px] text-sapphire-glow" />
                      </div>
                      <div className="font-display text-platinum text-[14px] leading-[1.2] tracking-tight">{l.label}</div>
                      <p className="mt-1.5 text-[11px] text-platinum/55 leading-[1.5] px-1">{l.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REAL OUTCOMES */}
      <section className="relative pb-20 lg:pb-24">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <h2 className="font-display text-center text-platinum text-[28px] sm:text-[34px] lg:text-[40px] leading-tight tracking-[-0.018em] fade-up">
            Real Outcomes. <span className="text-platinum/60">For Every Organization.</span>
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {OUTCOMES.map((o, i) => (
              <a key={o.slug} href={`/solutions/${o.slug}`} data-testid={`outcome-${o.slug}`}
                className="group relative rounded-2xl overflow-hidden border border-white/[0.07] hover:border-sapphire-glow/40 transition-all duration-500 fade-up"
                style={{ background: "linear-gradient(180deg, rgba(15,22,40,0.55) 0%, rgba(10,15,26,0.92) 100%)", animationDelay: `${i * 80}ms` }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 36px -8px rgba(96,165,250,0.40)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}>
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/45 to-transparent" />
                <div className="relative h-[150px] overflow-hidden">
                  <img src={o.img} alt={o.title}
                    className="block w-full h-full object-cover object-center transition-transform duration-[1200ms] group-hover:scale-[1.06]"
                    style={{
                      WebkitMaskImage: "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.55) 80%, transparent 100%)",
                      maskImage: "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.55) 80%, transparent 100%)",
                    }} draggable="false" />
                  <div className="absolute inset-0 pointer-events-none"
                    style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.30) 0%, rgba(10,15,26,0) 30%, rgba(10,15,26,0) 60%, rgba(10,15,26,0.95) 100%)" }} aria-hidden />
                </div>
                <div className="p-5 lg:p-6">
                  <div className="font-display text-platinum text-[18px] tracking-tight mb-2">{o.title}</div>
                  <p className="text-[12.5px] text-platinum/65 leading-[1.6] mb-4">{o.desc}</p>
                  <div className="text-[10.5px] tracking-[0.20em] uppercase text-sapphire-glow font-medium mb-2">Key Impact</div>
                  <ul className="space-y-1.5">
                    {o.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-[12px] text-platinum/72">
                        <CheckCircle2 className="w-3.5 h-3.5 mt-[2px] text-sapphire-glow shrink-0" /><span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </a>
            ))}

            {/* Experience The Esthera Advantage — final crystal card */}
            <div className="group relative rounded-2xl overflow-hidden border border-white/[0.08] fade-up min-h-[400px]"
              style={{ background: "linear-gradient(135deg, rgba(13,30,62,0.55) 0%, rgba(10,15,26,0.92) 60%, rgba(10,15,26,0.95) 100%)", animationDelay: "320ms" }}>
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/55 to-transparent" />
              {/* Crystal asset on the right */}
              <div className="absolute inset-y-0 right-0 w-[60%] pointer-events-none" style={{ transform: ctaParallax, willChange: "transform" }}>
                <div className="absolute inset-0"
                  style={{ background: "radial-gradient(50% 55% at 60% 50%, rgba(37,99,235,0.40), transparent 75%)", filter: "blur(36px)" }} aria-hidden />
                <img src={WHY_ESTHERA_IMAGES.ctaCrystal} alt="" aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover object-center breathe-glow"
                  style={{
                    mixBlendMode: "screen",
                    filter: "brightness(0.95) contrast(2.30) saturate(1.20)",
                    WebkitMaskImage: "radial-gradient(95% 100% at 60% 50%, #000 55%, rgba(0,0,0,0.55) 80%, transparent 100%)",
                    maskImage: "radial-gradient(95% 100% at 60% 50%, #000 55%, rgba(0,0,0,0.55) 80%, transparent 100%)",
                  }} draggable="false" />
                <div className="absolute inset-0"
                  style={{ background: "linear-gradient(90deg, rgba(10,15,26,0.65) 0%, rgba(10,15,26,0.15) 30%, rgba(10,15,26,0) 60%, rgba(10,15,26,0.30) 100%)" }} />
              </div>
              <div className="relative z-10 p-5 lg:p-6 flex flex-col h-full">
                <div className="font-display text-platinum text-[18px] lg:text-[20px] leading-[1.2] tracking-tight mb-2">
                  Experience The <span className="text-sapphire-glow" style={{ textShadow: "0 0 24px rgba(96,165,250,0.45)" }}>Esthera Advantage</span>
                </div>
                <p className="text-[12.5px] text-platinum/65 leading-[1.6] mb-5 max-w-[260px]">
                  Join the real estate leaders who trust Esthera RealityOS to operate smarter, move faster, and grow stronger.
                </p>
                <div className="mt-auto flex flex-col gap-2">
                  <button onClick={() => navigate("/book-demo")} data-testid="why-outcome-book-demo"
                    className="btn-primary rounded-md h-10 px-4 text-[12.5px] font-medium inline-flex items-center justify-between gap-2">
                    Book a Demo <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => navigate("/platform")} data-testid="why-outcome-explore"
                    className="btn-ghost rounded-md h-10 px-4 text-[12.5px] font-medium inline-flex items-center justify-between gap-2">
                    Explore The Platform <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
